import { graphlib } from "@dagrejs/dagre";
import { type GraphNode, type NodeElement, type ParsedRuleGraph, type Trigger, findNode } from "@packages/commons";
import { forEach } from "lodash";
import { validate as uuidValidate } from "uuid";

export interface FlowStep {
  id: string;
  containment: string;
  triggers: FlowTrigger[];
}

export interface FlowTrigger {
  input: string;
  actions: Trigger[];
  required?: boolean;
  decisive?: boolean;
  complex?: boolean;
}

export type TriggerStatus = "requiredDecisive" | "requiredNotDecisive" | "optionalDecisive" | "optionalNotDecisive";

export const getTriggerStatus = (trigger: FlowTrigger): TriggerStatus => {
  if (trigger.required) {
    return trigger.decisive ? "requiredDecisive" : "requiredNotDecisive";
  } else {
    return trigger.decisive ? "optionalDecisive" : "optionalNotDecisive";
  }
};

const findNodeWithDefault: typeof findNode = (graph, documents, descriptionOrId): GraphNode | undefined => {
  if (!descriptionOrId) return undefined as any;

  let node = findNode(graph, documents, descriptionOrId);
  if (!node && !uuidValidate(descriptionOrId)) {
    node = {
      description: descriptionOrId,
    } as GraphNode;
  }
  return node;
};

/*
const convertDecisionFlow = (flow, graph, docName, sectionId, existing) => {
  const rules: any[] = [];
  for (const n of flow.nodes()) {
    const block = flow.node(n);
    switch (block.type) {
      case "trigger": {
        if (!block.triggers) continue;
        // Check if the containment isn't defined elsewhere (which means we can't put it here - so this is an error state)
        let node = findNode(graph, undefined, block.containment);
        if (node) {
          // Check if this is defined in this section - which means we can preserve it
          const conc = findConclusion(node.description, existing);

          if (node.definedIn === docName && conc) {
            // it's defined here - for now I think we don't make any changes and just leave it as is
            rules.push(conc);
          } else {
            //console.log('not found', conc, block.containment, existing, node)
            // It's defined elsewhere - error state
            rules.push({
              type: "p",
              text: `Invalid flow: The rules for ${block.containment} task is defined already in another document or section. Either you need to change the name or bring those rules back into this rule block`,
            });
          }
        } else {
          const conclusion = {
            type: "conclusion",
            text: block.containment,
            sectionId,
            children: [
              {
                type: "operator",
                operator: block.required ? "all" : "any",
                children: block.triggers.map((t) => {
                  const input = findNodeWithDefault(graph, undefined, t.input);
                  if (input) {
                    return {
                      type: "condition",
                      text: block.required ? input.description : `not(${input.description})`,
                    };
                  }
                }),
              },
            ],
          };
          for (const t of block.triggers) {
            for (const out of t.actions) {
              const node = findNodeWithDefault(graph, undefined, out.outcome);
              if (node) {
                conclusion.children[0].children.push({
                  type: "condition",
                  text: block.decisive ? node.description : `${node.description} is known`,
                });
              }
            }
          }

          rules.push(conclusion);
        }
        // We need to add the input attributes, check if it doesn't already exist
        for (const t of block.triggers) {
          node = findNodeWithDefault(graph, undefined, t.input);
          // TODO: we need to check that the definition isn't inside this section (which means we can overwrite/merge)
          if (node && !node.id) {
            // We have to add it
            // Check to see if this trigger is connected to something (which would then be the condition for this)
            const parents = flow.predecessors(n);
            if (parents && parents.length > 0) {
              const conclusion: any = {
                type: "conclusion",
                text: node.description,
                sectionId,
                children: [
                  {
                    type: "operator",
                    operator: "all",
                    children: [],
                  },
                ],
              };
              for (const p of parents) {
                const node = flow.node(p);
                switch (node.type) {
                  case "trigger":
                    conclusion.children[0].children.push({
                      type: "condition",
                      text: findNodeWithDefault(graph, undefined, node.containment)?.description,
                    });
                    break;
                  default:
                    console.log(`Unknown parent trigger ${node.type}`);
                }
              }
              rules.push(conclusion);
            } else {
              rules.push({
                type: "conclusion",
                text: `${node.description} = true`,
                sectionId,
              });
            }
          } else if (node?.definedIn === docName) {
            // Check that to see if the attr is defined within this section, if so we can merge
            // Otherwise it's effectively as if it was defined in another document
            const conc = findConclusion(node?.description, existing);
            if (conc) {
              // It's defined in this section, so we return it as is
              rules.push(conc);
            }
          } // It already exists so we don't need to do anything
        }
        break;
      }
      case "attribute":
        // Ignore: this handled in the trigger
        break;
      default:
        console.log(`Unknown block type in flow: ${block.type}`);
    }
  }
  return rules;
};
*/

const findLCA = (searchNodes, graph) => {
  // https://www.baeldung.com/cs/lowest-common-ancestor-acyclic-graph
  // Algo
  // From each node mark a graph that contains it's parents
  // Then determine the intersection of these nodes
  const commonGraph = new graphlib.Graph();
  const traverseParents = (mark, node, connect = true, seen = new Set()) => {
    if (seen.has(node)) return;
    seen.add(node);
    const parents = graph.predecessors(node);
    if (parents && parents.length > 0) {
      for (const p of parents) {
        // Already marked?
        const common: any = commonGraph.node(p);
        if (!common) {
          commonGraph.setNode(p, {
            id: p,
            mark: [mark],
          });
        } else {
          common.mark.push(mark);
          commonGraph.setNode(p, common);
        }
        if (connect) commonGraph.setEdge(p, node);
        // Now process my ancestors
        traverseParents(mark, p, true, seen);
      }
    }
  };
  for (const n of searchNodes) {
    traverseParents(n, n, false);
  }

  // Now strip all nodes that aren't a common ancestor to all these nodes
  for (const n of commonGraph.nodes()) {
    const node = commonGraph.node(n);
    // @ts-ignore
    if (node.mark.length !== searchNodes.length) commonGraph.removeNode(n);
  }

  // Now the graph only contains those nodes that are a common ancestor;
  const lowest = commonGraph.sinks();
  // There could be multiple lowest ancestors, so return all of them in that case - the calling algo can determine what to do
  if (lowest && lowest.length === 1) return lowest[0];
  else return lowest;
};

const FLOW_LAYOUT_NODESEP = 80;
const FLOW_LAYOUT_RANKSEP = 120;

export const generateFlowGraph = (graph: ParsedRuleGraph, sectionId?: string, rootNodeId?: string) => {
  const flow = new graphlib.Graph();
  // Set graph options so dagre leaves enough space between nodes
  flow.setGraph({ rankdir: "LR", nodesep: FLOW_LAYOUT_NODESEP, ranksep: FLOW_LAYOUT_RANKSEP });
  //let distance = Graph.alg.dijkstraAll(graph);
  // Find all attribute that have triggers and process them
  for (const n of graph?.nodes() || []) {
    const node = graph.node(n);
    // Only include nodes in this section
    // If rootNodeId is provided, only include nodes that are the root node or reachable from it
    if (node?.triggers && (!sectionId || node.sectionId === sectionId)) {
      // If rootNodeId is specified, only process flows starting from that node
      // Ensure both are strings for proper comparison
      if (rootNodeId && typeof rootNodeId === "string" && typeof n === "string" && n !== rootNodeId) {
        continue;
      } else if (rootNodeId && (typeof rootNodeId !== "string" || typeof n !== "string")) {
        // If types don't match, skip to avoid issues
        continue;
      }
      //console.log('check', node)
      // It has triggers, which are a core part of the flow, lets add them

      // We need to determine if this already exists, but to do so we need the containment. But to get the containment we need the LCA.
      /*
      let element = {
        type: 'trigger',
        triggers: [],
        outputs: []
      }*/
      // Ensure node.id is a valid string before proceeding
      if (!node.id || typeof node.id !== "string") {
        console.log("Skipping node with invalid id", node.id);
        continue;
      }

      const searchNodes = [node.id];
      const actions: Trigger[] = [];

      for (const t of node.triggers) {
        // Each trigger t from node.triggers is itself a Trigger object
        // We need to preserve it, but update the outcome ID if we can find the node
        const outcome = findNode(graph, undefined, t.outcome);
        if (outcome?.id && typeof outcome.id === "string") {
          // Update the outcome ID and preserve all other properties
          actions.push({
            ...t,
            outcome: outcome.id,
          });
          searchNodes.push(outcome.id);
        } else {
          // Even if we can't find the outcome node, preserve the trigger as-is
          // This ensures actions and other properties are not lost
          actions.push(t);
          // Still try to add the outcome ID to searchNodes if it exists (even if node not found)
          if (t.outcome) {
            searchNodes.push(t.outcome);
          }
        }
      }
      //if (searchNodes.length < 2) continue; // This trigger has no outcomes we could find. Won't be able to find an LCA and thus won't get a containment - bad rules most likely
      // Now find the lowest common ancestor of these nodes. This will be the containment node
      const commonParent = findLCA(searchNodes, graph);
      if (Array.isArray(commonParent)) {
        console.log("TODO: handle multiple LCA", commonParent, searchNodes);
        continue;
      } else if (!commonParent) {
        console.log("TODO: handle no common parent");
        continue;
      }
      // We have a common parent - this is the containment attribute
      // Now check if this exists in the flow already
      let element: any = flow.node(commonParent);
      // If it doesn't exist yet or it was previously not a trigger (most likely an attribute) then reset to look like a blank trigger
      if (!element || element.type !== "trigger")
        element = {
          type: "trigger",
          triggers: [],
        };
      // Ensure node.id is a valid string
      if (!node.id || typeof node.id !== "string") {
        console.log("Invalid node.id, skipping trigger", node.id);
        continue;
      }

      element.triggers.push({
        input: node.id,
        actions: actions,
      });
      element.containment = commonParent;
      // Determine if required/decisive or complex
      // We can only know this if the rules are designed the way we'd expect
      // Otherwise complex
      // @ts-ignore
      const containment = graph.node(commonParent);

      if (!containment) {
        console.log("TODO: handle containment not found", commonParent);
        continue;
      }

      let isValid = false;
      let required: any;
      let decisive: any;
      //console.log('el', JSON.stringify(element, null, 2));
      if (containment.conditions) {
        const traverse = (attr: string, el: Partial<NodeElement>, parent?: any) => {
          if (el.type === "attribute" && el.attributeId === attr) return parent;
          else if (el.elements) {
            let outcome: any;
            forEach(el.elements, (subEl) => {
              const sub = traverse(attr, subEl, el);
              if (sub) {
                // Send the parent
                outcome = sub;
                return false;
              }
            });
            return outcome;
          }
        };

        for (const t of element.triggers) {
          t.required = false;
          t.decisive = false;
          t.complex = false;

          // Check if the input is required or not
          // Find the definition of this input within the conditions
          const parentRule = traverse(t.input, { elements: containment.conditions });

          if (parentRule && parentRule.type === "not") t.required = false;
          else if (parentRule && (parentRule.type === "or" || parentRule.type === "and")) t.required = true;
          else t.complex = true;

          // Now to determine decisive we need to check the outcomes, which there could be multiple of
          for (const a of t.actions) {
            const parentRule = traverse(a.outcome, { elements: containment.conditions });
            if (parentRule && parentRule.type === "known") t.decisive = false;
            else if (parentRule && (parentRule.type === "or" || parentRule.type === "and")) t.decisive = true;
            else t.complex = true;
          }
        }
        if (typeof required !== "undefined" && typeof decisive !== "undefined") isValid = true;
      }
      /*
      if (containment.conditions) { // TODO: should we support decision tables?
        let operator = get(containment.conditions, '0.type');
        console.log('operator', operator, t);
        if (operator === 'and' || operator === 'or') {
          required = operator === 'and' ? true : false;
          let el1 = get(containment.conditions, '0.elements.0');
          let el2 = get(containment.conditions, '0.elements.1');

          // Check that the first entry is the input node
          if ((el1.type === 'not' && get(containment.conditions, `0.elements.0.elements.0.attributeId`) === t.input) || (el1.type === 'attribute' && el1.attributeId === t.input)) {
            if (element.outputs.length === 1) {
              // if they have multiple outputs the decisive is not possible to determine
              // Check the single output
              if (el2.type === 'known' && get(containment.conditions, `0.elements.0.elements.0.attributeId`) === element.outputs[0].attribute) {
                decisive = false;
              } else if (el2.type === 'attribute' && el2.attributeId === element.outputs[0].attribute) {
                decisive = true;
              }
              if (typeof decisive === 'boolean') isValid = true;
            }
          }
        }
      }*/

      //console.log('isvalid', isValid, required, decisive)

      // Ensure containment is a valid string before using it as a node ID
      if (!commonParent || typeof commonParent !== "string") {
        console.log("Invalid commonParent, skipping node creation", commonParent);
        continue;
      }

      element.id = element.containment;
      element.height = 100 + element.triggers.length * 200;
      element.width = 320;
      flow.setNode(element.containment, element);
      // We also need to add any children to the 'input' attributes
      for (const t of element.triggers) {
        const children = graph.successors(t.input);
        if (children) {
          for (const c of children) {
            const existingFlowNode: any = flow.node(c);
            let id: string | undefined;
            if (existingFlowNode) {
              // It already exists - do nothing?
              if (existingFlowNode.type === "attribute") id = existingFlowNode.id;
              else if (existingFlowNode.type === "trigger") id = existingFlowNode.containment;
              else console.log("unknown node type in edge connection", existingFlowNode);
            } else {
              // Ensure c is a valid string before using it as a node ID
              if (!c || typeof c !== "string") {
                console.log("Skipping invalid child node ID", c);
                continue;
              }
              // Get the actual node from the original graph to get its description
              const originalNode = graph.node(c);
              const nodeDescription = originalNode?.description || c || "1";
              const descLen = String(nodeDescription).length;
              flow.setNode(c, {
                type: "attribute",
                width: Math.max(220, Math.min(400, 80 + descLen * 6)),
                height: Math.max(98, (descLen / 32) * 17.5 + 20),
                id: c,
              });
              id = c;
            }
            //console.log('setting', id, node, element.containment)
            // Ensure both IDs are valid strings before creating the edge
            // Also ensure t.input is a valid string for the edge targetHandle
            if (
              id &&
              typeof id === "string" &&
              typeof element.containment === "string" &&
              typeof t.input === "string"
            ) {
              flow.setEdge(id, element.containment, { targetHandle: t.input });
            } else {
              console.log("Skipping edge creation due to invalid IDs or input", {
                id,
                containment: element.containment,
                input: t.input,
              });
            }
          }
        }
      }
    }
  }

  // Validate all node IDs are strings and node data is valid before returning
  const nodeIds = flow.nodes();
  for (const nodeId of nodeIds) {
    if (typeof nodeId !== "string") {
      console.error("Invalid node ID found in flow graph:", nodeId);
      flow.removeNode(nodeId);
      continue;
    }
    const nodeData: any = flow.node(nodeId);
    if (nodeData) {
      // Ensure all string properties are actually strings
      if (nodeData.id !== undefined && typeof nodeData.id !== "string") {
        console.error("Invalid node.id type:", nodeData.id, "for node:", nodeId);
        nodeData.id = String(nodeData.id || nodeId);
      }
      if (nodeData.type !== undefined && typeof nodeData.type !== "string") {
        console.error("Invalid node.type:", nodeData.type, "for node:", nodeId);
        nodeData.type = String(nodeData.type || "node");
      }
      if (nodeData.containment !== undefined && typeof nodeData.containment !== "string") {
        console.error("Invalid node.containment:", nodeData.containment, "for node:", nodeId);
        nodeData.containment = String(nodeData.containment || nodeId);
      }
    }
  }

  // Validate all edge source/target IDs are strings
  const edges = flow.edges();
  for (const edge of edges) {
    if (typeof edge.v !== "string" || typeof edge.w !== "string") {
      console.error("Invalid edge found in flow graph:", edge);
      flow.removeEdge(edge.v, edge.w);
      continue;
    }
    // Validate edge label if it exists
    const edgeData = flow.edge(edge);
    if (edgeData && edgeData.label !== undefined && typeof edgeData.label !== "string") {
      console.error("Invalid edge label:", edgeData.label, "for edge:", edge);
      flow.setEdge(edge.v, edge.w, { ...edgeData, label: String(edgeData.label || "") });
    }
  }

  return flow;
};

export interface ReleaseMapping {
  id?: string;
  name: string;
  mode?: "json" | "csv";
  data?: Record<string, unknown>;
}

export interface ReleaseInterview {
  id: string;
  goal?: string;
  name?: string;
}

export interface ReleaseWithMapping {
  mapping?: ReleaseMapping[];
  interviews?: ReleaseInterview[];
}

/** Recursively collect all string keys from a data object (objects and arrays, all depths). */
function collectDataKeys(obj: unknown): Set<string> {
  const keys = new Set<string>();
  function visit(val: unknown): void {
    if (val === null || val === undefined) {
      return;
    }
    if (Array.isArray(val)) {
      for (const item of val) {
        visit(item);
      }
      return;
    }
    if (typeof val === "object") {
      for (const k of Object.keys(val)) {
        keys.add(k);
        visit((val as Record<string, unknown>)[k]);
      }
    }
  }
  visit(obj);
  return keys;
}

/** Find a node in the parsed graph whose description matches the given key (exact match). */
function findNodeByDescription(
  parsedGraph: graphlib.Graph,
  key: string,
): { id: string; description?: string } | undefined {
  for (const nid of parsedGraph.nodes()) {
    const node = parsedGraph.node(nid) as { description?: string; id?: string } | undefined;
    if (node?.description === key && node?.id) {
      return { id: node.id, description: node.description };
    }
  }
  return undefined;
}

/** Check if targetNodeId is reachable from sourceNodeId via successors (children) in the graph. Uses BFS. */
function isReachableViaSuccessors(
  graph: graphlib.Graph,
  sourceNodeId: string,
  targetNodeId: string,
  maxDepth = 100,
): boolean {
  if (sourceNodeId === targetNodeId) {
    return true;
  }
  const visited = new Set<string>();
  const queue: { id: string; depth: number }[] = [{ id: sourceNodeId, depth: 0 }];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current.id) || current.depth > maxDepth) {
      continue;
    }
    visited.add(current.id);

    const successors = graph.successors(current.id);
    if (!successors) {
      continue;
    }
    for (const succ of successors) {
      if (succ === targetNodeId) {
        return true;
      }
      if (!visited.has(succ)) {
        queue.push({ id: succ, depth: current.depth + 1 });
      }
    }
  }
  return false;
}

/** Enriches the flow graph with mapping nodes and mapping→attribute edges from mapping.data. */
export const enrichFlowGraphWithMappingNodes = (
  flowGraph: graphlib.Graph,
  release: ReleaseWithMapping | null | undefined,
  parsedGraph: graphlib.Graph | null | undefined,
): graphlib.Graph => {
  if (!release) {
    return flowGraph;
  }
  const hasMapping = Boolean(release.mapping?.length);
  const hasInterviews = Boolean(release.interviews?.length);
  if (!hasMapping && !hasInterviews) {
    return flowGraph;
  }

  const mappingById = new Map<string, ReleaseMapping>();
  for (const m of release.mapping ?? []) {
    const id = m.id ?? (m as any).name;
    if (id) {
      mappingById.set(id, m);
    }
  }

  const interviewById = new Map<string, ReleaseInterview>();
  if (release.interviews?.length) {
    for (const i of release.interviews) {
      if (i.id) {
        interviewById.set(i.id, i);
      }
    }
  }

  const enriched = new graphlib.Graph();
  const graphOpts = flowGraph.graph();
  enriched.setGraph({
    rankdir: graphOpts.rankdir ?? "LR",
    nodesep: graphOpts.nodesep ?? FLOW_LAYOUT_NODESEP,
    ranksep: graphOpts.ranksep ?? FLOW_LAYOUT_RANKSEP,
    ranker: graphOpts.ranker ?? "tight-tree",
  });

  for (const nodeId of flowGraph.nodes()) {
    const nodeData = flowGraph.node(nodeId) as any;
    if (nodeData) {
      enriched.setNode(nodeId, { ...nodeData });
    }
  }

  for (const edge of flowGraph.edges()) {
    const edgeData = flowGraph.edge(edge);
    if (typeof edgeData === "string") {
      enriched.setEdge(edge.v, edge.w, { id: edgeData, targetHandle: edgeData });
    } else {
      enriched.setEdge(edge.v, edge.w, edgeData || {});
    }
  }

  const existingFlowNodeIds = new Set(flowGraph.nodes());

  for (const nodeId of flowGraph.nodes()) {
    const nodeData = enriched.node(nodeId) as any;
    if (nodeData?.type !== "trigger" || !nodeData.triggers?.length) {
      continue;
    }

    const mappingIdsUsed = new Set<string>();
    for (const trigger of nodeData.triggers) {
      for (const action of trigger.actions ?? []) {
        const responseMappingId = action?.assignmentIntegration?.responseMapping;
        if (responseMappingId && mappingById.has(responseMappingId)) {
          mappingIdsUsed.add(responseMappingId);
        }
      }
    }

    for (const mappingId of mappingIdsUsed) {
      const mapping = mappingById.get(mappingId);
      if (!mapping) {
        continue;
      }
      const mappingNodeId = `mapping:${mappingId}`;
      if (!enriched.node(mappingNodeId)) {
        enriched.setNode(mappingNodeId, {
          type: "mapping",
          id: mappingNodeId,
          mappingId,
          name: mapping.name,
          mode: mapping.mode ?? "json",
          height: 88,
          width: 220,
        });
      }
    }

    // Per-action edges: shared handle for task actions (mapping + interview goal)
    nodeData.triggers.forEach((trigger: { input: string; actions?: any[] }) => {
      (trigger.actions ?? []).forEach((action: any, actionIndex: number) => {
        if (action?.type !== "task") {
          return;
        }
        const handleId = `task-${nodeId}-${trigger.input}-${actionIndex}`;
        const responseMappingId = action?.assignmentIntegration?.responseMapping;
        if (responseMappingId && mappingById.has(responseMappingId)) {
          const mappingNodeId = `mapping:${responseMappingId}`;
          enriched.setEdge(nodeId, mappingNodeId, {
            id: `responseMapping:${nodeId}:${responseMappingId}:${actionIndex}`,
            sourceHandle: handleId,
            targetHandle: "mapping",
            label: "Uses",
          });
        }
        const interviewId = action?.interview;
        if (interviewId && interviewById.has(interviewId) && parsedGraph) {
          const interview = interviewById.get(interviewId)!;
          const goalNodeId = interview.goal;
          if (goalNodeId && parsedGraph.node(goalNodeId)) {
            if (!enriched.node(goalNodeId)) {
              const originalNode = parsedGraph.node(goalNodeId) as { description?: string } | undefined;
              const nodeDescription = originalNode?.description ?? goalNodeId;
              const descLen = String(nodeDescription).length;
              enriched.setNode(goalNodeId, {
                type: "attribute",
                width: Math.max(220, Math.min(400, 80 + descLen * 6)),
                height: Math.max(98, (descLen / 32) * 17.5 + 20),
                id: goalNodeId,
              });
            }
            enriched.setEdge(nodeId, goalNodeId, {
              id: `interviewGoal:${nodeId}:${interviewId}:${actionIndex}`,
              sourceHandle: handleId,
              label: "Interview Goal",
            });
          }
        }
      });
    });
  }

  // Track which trigger nodes use which mappings and their input nodes
  const triggerMappingInfo: Array<{
    triggerNodeId: string;
    inputNodeIds: string[];
    mappingIds: string[];
  }> = [];

  for (const nodeId of flowGraph.nodes()) {
    const nodeData = flowGraph.node(nodeId) as any;
    if (nodeData?.type !== "trigger" || !nodeData.triggers?.length) {
      continue;
    }
    const inputNodeIds: string[] = [];
    const mappingIds: string[] = [];
    for (const trigger of nodeData.triggers) {
      if (trigger.input) {
        inputNodeIds.push(trigger.input);
      }
      for (const action of trigger.actions ?? []) {
        const responseMappingId = action?.assignmentIntegration?.responseMapping;
        if (responseMappingId && mappingById.has(responseMappingId)) {
          mappingIds.push(responseMappingId);
        }
      }
    }
    if (inputNodeIds.length > 0 && mappingIds.length > 0) {
      triggerMappingInfo.push({ triggerNodeId: nodeId, inputNodeIds, mappingIds });
    }
  }
  // Collect mapping output attribute node IDs per mapping
  const mappingOutputNodes = new Map<string, Set<string>>();

  if (parsedGraph && typeof parsedGraph.node === "function") {
    for (const nodeId of enriched.nodes()) {
      const nodeData = enriched.node(nodeId) as any;
      if (nodeData?.type !== "mapping" || !nodeData.mappingId) {
        continue;
      }
      const mapping = mappingById.get(nodeData.mappingId) as ReleaseMapping | undefined;
      if (!mapping?.data || typeof mapping.data !== "object") {
        continue;
      }
      const dataKeys = collectDataKeys(mapping.data);
      const outputIds = new Set<string>();
      for (const key of dataKeys) {
        const matched = findNodeByDescription(parsedGraph, key);
        if (!matched) {
          continue;
        }
        const attrNodeId = matched.id;
        outputIds.add(attrNodeId);
        const alreadyInFlow = existingFlowNodeIds.has(attrNodeId);
        if (!enriched.node(attrNodeId)) {
          const originalNode = parsedGraph.node(attrNodeId) as { description?: string } | undefined;
          const nodeDescription = originalNode?.description ?? attrNodeId;
          const descLen = String(nodeDescription).length;
          enriched.setNode(attrNodeId, {
            type: "attribute",
            width: Math.max(220, Math.min(400, 80 + descLen * 6)),
            height: Math.max(98, (descLen / 32) * 17.5 + 20),
            id: attrNodeId,
          });
        }
        enriched.setEdge(nodeId, attrNodeId, {
          id: `mappingData:${key}`,
          sourceHandle: "data",
          bad: alreadyInFlow,
          label: "Updates",
        });
      }
      mappingOutputNodes.set(nodeData.mappingId, outputIds);
    }

    // Check for connections between trigger inputs and mapping outputs via the parsed graph
    for (const { triggerNodeId, inputNodeIds, mappingIds } of triggerMappingInfo) {
      const allOutputNodeIds = new Set<string>();
      for (const mid of mappingIds) {
        const outputs = mappingOutputNodes.get(mid);
        if (outputs) {
          for (const o of outputs) {
            allOutputNodeIds.add(o);
          }
        }
      }
      // Find child nodes of the trigger: nodes that have an edge INTO the trigger with targetHandle = inputNodeId
      // (the flow graph shows these children connecting to the trigger, not the input node itself)
      const inputToChildNodes = new Map<string, string[]>();
      const inEdges = enriched.inEdges(triggerNodeId);
      if (inEdges) {
        for (const edge of inEdges) {
          const edgeData = enriched.edge(edge) as { targetHandle?: string } | undefined;
          const targetHandle = edgeData?.targetHandle;
          if (targetHandle && inputNodeIds.includes(targetHandle)) {
            const childNodeId = edge.v;
            if (!inputToChildNodes.has(targetHandle)) {
              inputToChildNodes.set(targetHandle, []);
            }
            inputToChildNodes.get(targetHandle)!.push(childNodeId);
          }
        }
      }

      // For each input node, check if any output node is reachable via successors; if so, add warning edge
      // from mapping output (right) to child/input node (left) so the line comes out of the right node and into the left
      for (const inputNodeId of inputNodeIds) {
        const childNodeIds = inputToChildNodes.get(inputNodeId) ?? [];
        for (const outputNodeId of allOutputNodeIds) {
          if (!isReachableViaSuccessors(parsedGraph, inputNodeId, outputNodeId)) {
            continue;
          }
          const targetNodeIds = childNodeIds.length > 0 ? childNodeIds : [inputNodeId];
          for (const targetNodeId of targetNodeIds) {
            if (!enriched.node(targetNodeId)) {
              continue;
            }
            const edgeKey = `warning:${outputNodeId}:${targetNodeId}`;
            if (enriched.edge(outputNodeId, targetNodeId)) {
              continue;
            }
            enriched.setEdge(outputNodeId, targetNodeId, {
              id: edgeKey,
              bad: true,
              label: "Warning: Connected",
            });
          }
        }
      }
    }
  }

  return enriched;
};

/*const findConclusion = (concText, rules) => {
  let conc;
  const traverse = (el) => {
    if (el.type === "conclusion" && el.text.includes(concText)) {
      conc = el;
    } else if (el.children) {
      for (const c of el.children) {
        traverse(c);
      }
    }
  };
  if (rules && Array.isArray(rules)) {
    for (const r of rules) {
      traverse(r);
    }
  }
  return conc;
};*/
